import { useFonts } from "expo-font";
import { Shrikhand_400Regular } from "@expo-google-fonts/shrikhand";
import {
  NunitoSans_400Regular,
  NunitoSans_600SemiBold,
  NunitoSans_700Bold,
  NunitoSans_800ExtraBold,
} from "@expo-google-fonts/nunito-sans";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { Stack } from "expo-router";
import * as SplashScreen from "expo-splash-screen";
import { AppLifecycle, useStartupUpdateGate } from "~/lib/app-updates";
import { BackendStatusOverlay } from "~/components/BackendStatusOverlay";
import { StatusBar } from "expo-status-bar";
import { useEffect, type ReactNode } from "react";
import * as Sentry from "@sentry/react-native";
import { MfaGate } from "~/components/MfaGate";
import { LocalDataFrame } from "~/components/LocalDataBanner";
import { WebAccessGate } from "~/components/WebAccessGate";
import { AuthProvider, useAuth } from "~/lib/auth";
import { QuerySessionBoundary } from "~/components/QuerySessionBoundary";
import { queryClient } from "~/lib/query-client";
import { persistOptions } from "~/lib/query-persist";
import { usePushRegistration } from "~/features/notifications/push-registration";
import { ThemeProvider, useTheme } from "~/styles/theme";

void SplashScreen.preventAutoHideAsync();

function AppReady({ children }: { children: ReactNode }) {
  const { ready } = useAuth();

  usePushRegistration();

  if (!ready) return null;
  return <>{children}</>;
}

function ThemedStack() {
  const { scheme, colors } = useTheme();
  return (
    <>
      <StatusBar style={scheme === "dark" ? "light" : "dark"} />
      <Stack
        screenOptions={{
          headerShown: false,
          contentStyle: { backgroundColor: colors.background },
          headerStyle: { backgroundColor: colors.surface },
          headerTintColor: colors.primary,
          headerShadowVisible: false,
        }}
      >
        <Stack.Screen name="(tabs)" />
        <Stack.Screen
          name="recipe/[id]/index"
          options={{ headerShown: true, title: "" }}
        />
        <Stack.Screen
          name="recipe/[id]/cook"
          options={{ headerShown: true, title: "Cooking Mode" }}
        />
        <Stack.Screen name="auth" options={{ presentation: "modal" }} />
        <Stack.Screen
          name="notifications"
          options={{ presentation: "modal" }}
        />
        <Stack.Screen
          name="support/index"
          options={{ headerShown: true, title: "Support" }}
        />
        <Stack.Screen
          name="support/[id]"
          options={{ headerShown: true, title: "Ticket" }}
        />
      </Stack>
    </>
  );
}

function RootLayout() {
  const [fontsLoaded, fontError] = useFonts({
    Shrikhand_400Regular,
    NunitoSans_400Regular,
    NunitoSans_600SemiBold,
    NunitoSans_700Bold,
    NunitoSans_800ExtraBold,
  });

  // Hold the splash until fonts load and the launch-time update check finishes (4s cap).
  const updateCheckDone = useStartupUpdateGate();
  const ready = (fontsLoaded || Boolean(fontError)) && updateCheckDone;

  useEffect(() => {
    if (ready) void SplashScreen.hideAsync();
  }, [ready]);

  if (!ready) return null;

  return (
    <ThemeProvider>
      <AppLifecycle />
      <BackendStatusOverlay />
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={persistOptions}
      >
        <AuthProvider>
          <MfaGate>
            <QuerySessionBoundary>
              <AppReady>
                <WebAccessGate>
                  <LocalDataFrame>
                    <ThemedStack />
                  </LocalDataFrame>
                </WebAccessGate>
              </AppReady>
            </QuerySessionBoundary>
          </MfaGate>
        </AuthProvider>
      </PersistQueryClientProvider>
    </ThemeProvider>
  );
}

export default Sentry.wrap(RootLayout);
